import styled, { css } from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';

type DsaInfoProps = {
  dsaInfo: DsaInfo;
  countryId: string;
} & React.HTMLAttributes<HTMLDivElement>;

// #region styles
const BoldText = styled.span`
  font-weight: bold;
`;

const NewParagraph = styled.p`
  padding-top: 8px;
`;
// #endregion

function hasTraderData(dsaInfo: DsaInfo | undefined) {
  const { phone, registrationNumber, address, name, email, tradeRegister } = dsaInfo || {};

  return Boolean(
    name ||
    email ||
    phone ||
    tradeRegister ||
    registrationNumber ||
    (address && (address.street || address.city || address.postalCode || address.country))
  );
}

function hasLicenseNumbers(dsaInfo: DsaInfo | undefined) {
  return Boolean(dsaInfo?.licenseNumbers?.length);
}

// travel-api passes its `dsa` object through untouched, so it can arrive present but hollow. The
// block below is a bordered, padded container, and callers decide whether to give it a section
// heading — both would otherwise draw around nothing.
export function hasDsaContent(dsaInfo: DsaInfo | undefined) {
  return hasTraderData(dsaInfo) || hasLicenseNumbers(dsaInfo);
}

export function DsaInfo({ dsaInfo, countryId, ...rest }: DsaInfoProps) {
  const { licenseNumbers, phone, registrationNumber, address, name, email, tradeRegister } = dsaInfo || {};
  const { t } = useTranslation();

  return (
    hasDsaContent(dsaInfo) && (
      <div
        {...rest}
        css={css`
          line-height: 1rem;
          font-size: ${(p) => p.theme.kallisto.font.size['175']};
          color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};
          padding-top: 8px;
          border-top: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
        `}
      >
        {hasTraderData(dsaInfo) && (
          <div>
            <BoldText>{`${t('vacation.select.dsa.title')}: `}</BoldText>
            {name && `${t('vacation.select.dsa.legal_name')}: ${name}, `}
            {address && (
              <>
                {address.street && `${t('vacation.select.dsa.address')}: ${address.street}, `}
                {address.city && `${address.city}, `}
                {address.postalCode && `${address.postalCode}, `}
                {address.country && `${address.country}, `}
              </>
            )}
            {email && `${t('vacation.select.dsa.email')}: ${email}, `}
            {phone && `${t('vacation.select.dsa.telephone_number')}: ${phone}, `}
            {tradeRegister && `${t('vacation.select.dsa.trade_register')}: ${tradeRegister}, `}
            {registrationNumber && `${t('vacation.select.dsa.registration_number')}: ${registrationNumber}, `}
          </div>
        )}
        {hasLicenseNumbers(dsaInfo) && (
          <NewParagraph>
            {licenseNumbers && licenseNumbers.length > 0 && (
              <>
                <BoldText>{`${countryId === 'IT' ? t('vacation.select.dsa.license_number_and_CIN') : t('vacation.select.dsa.license_number')}:`}</BoldText>{' '}
                {licenseNumbers.join(' / ')}
              </>
            )}
          </NewParagraph>
        )}
      </div>
    )
  );
}
